import { Inject, Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { ActivatableItem, SelectedItem, SelectedItems, SortDirection } from '../../../activation/core/interface';
import {
  doItemsHaveRestrictionReasons,
  filterRestrictedItems,
  pageArray,
  searchActivatableItems,
  sortActivatableItems,
} from '../../../activation/core/utils';
import { Activation } from '../../../core/account.service';
import { ActivationPricingServiceInterface } from './activation-pricing-service-interface';
import { ActivationServiceInterface } from './activation-service-interface';
import { ActivationPricingServiceInterfaceToken, ActivationServiceInterfaceToken } from './tokens';

@Injectable({
  providedIn: 'root',
})
export class ActivateAppTableService {
  readonly activatableItems$: Observable<ActivatableItem[]>;
  readonly selectedItems$: Observable<SelectedItems>;
  readonly unavailableCount$: Observable<number>;
  readonly totalItems$: Observable<number>;
  readonly areItemsUnavailable$: Observable<boolean>;
  readonly pageOptions$: Observable<number[]>;
  readonly hidePricing$: Observable<boolean>;
  readonly activations$: Observable<Activation[]>;

  private nameSort$$: BehaviorSubject<SortDirection> = new BehaviorSubject<SortDirection>('ASCENDING');
  private showRestricted$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private paged$$: BehaviorSubject<PageEvent> = new BehaviorSubject<PageEvent>(undefined);

  get showingRestrictedProducts$(): Observable<boolean> {
    return this.showRestricted$$.asObservable();
  }

  // Please note, the 2 interfaces are set up to allow use to inject the services. By injecting these 2 tokens, we can switch out the
  // services that provide data to the table, and allow more generic of use cases.
  constructor(
    @Inject(ActivationServiceInterfaceToken) private activationService: ActivationServiceInterface,
    @Inject(ActivationPricingServiceInterfaceToken) private activationPricingService: ActivationPricingServiceInterface,
  ) {
    this.selectedItems$ = this.activationService.selectedItems$;
    this.hidePricing$ = this.activationPricingService.hidePricing$;
    this.pageOptions$ = of([25, 50, 100, 200, 500]);
    const unfilteredActivatableItems$ = this.activationService.activatableItems$;
    const filteredActivatableItems$ = unfilteredActivatableItems$.pipe(
      map((activatableItems) => {
        return filterRestrictedItems(activatableItems);
      }),
      shareReplay(1),
    );

    const restrictedActivatableItems$ = combineLatest([
      filteredActivatableItems$,
      unfilteredActivatableItems$,
      this.showRestricted$$,
    ]).pipe(
      map(([filtered, unfiltered, showRestricted]) => {
        return showRestricted ? unfiltered : filtered;
      }),
    );

    this.unavailableCount$ = combineLatest([filteredActivatableItems$, unfilteredActivatableItems$]).pipe(
      map(([filtered, unfiltered]) => {
        return unfiltered.length - filtered.length;
      }),
    );

    this.areItemsUnavailable$ = unfilteredActivatableItems$.pipe(
      map((activatableItems) => {
        return doItemsHaveRestrictionReasons(activatableItems);
      }),
      shareReplay(1),
    );

    // searchTermDelayed$ used to reduce the number of times we search the list
    // (useful to not make repeated calls while a user is typing)
    const searchTermDelayed$ = this.searchTerm$$.pipe(debounceTime(200), distinctUntilChanged()) as Observable<string>;
    const searchedActivatableItems$ = combineLatest([restrictedActivatableItems$, searchTermDelayed$]).pipe(
      map(([activatableItems, searchTerm]) => {
        return searchActivatableItems(activatableItems, searchTerm);
      }),
      shareReplay(1),
    );

    this.totalItems$ = searchedActivatableItems$.pipe(map((items) => (items ? items.length : 0)));

    const sortedActivatableItems$ = combineLatest([searchedActivatableItems$, this.nameSort$$]).pipe(
      map(([activatableItems, sortDirection]) => {
        return sortActivatableItems(activatableItems, sortDirection);
      }),
      shareReplay(1),
    );

    this.activatableItems$ = combineLatest([sortedActivatableItems$, this.paged$$]).pipe(
      map(([sortedItems, pageEvent]) => {
        return pageArray(sortedItems, pageEvent, 50);
      }),
    );

    this.activations$ = this.activationService.activations$;
  }

  sortByName(order: SortDirection): void {
    this.nameSort$$.next(order);
  }

  setSearchTerm(searchTerm: string): void {
    this.searchTerm$$.next(searchTerm);
  }

  setPage(page: PageEvent): void {
    this.paged$$.next(page);
  }

  setShowingRestrictedProducts(showRestricted: boolean): void {
    this.showRestricted$$.next(showRestricted);
  }

  selectItem(selectedItem: SelectedItem): void {
    this.activationService.selectItem(selectedItem);
  }

  toggleShowingRestricted(): void {
    this.setShowingRestrictedProducts(!this.showRestricted$$.getValue());
  }
}
